import { EVENT_KEY } from '../../feed/drawerUrl';

/**
 * The `?tab=` deep link into a machine's asset drawer.
 *
 * The drawer has no route of its own: it is open exactly when the Summary route points at a machine
 * (`summary/index.tsx`), so a link that wants a specific tab carries it in the query string. Both
 * ends of that contract live here so a link and its reader cannot drift apart.
 */

import type { EventTabId } from '../../overview/assetOverview/components/maintenanceEventModal';

export type AssetDrawerTab = 'detail' | 'history' | 'chat';

const TABS: readonly AssetDrawerTab[] = ['detail', 'history', 'chat'];
// Spelled out instead of read from the modal: AppMain imports this file eagerly, and a runtime
// import would pull the whole modal into the main bundle. The Record makes the compiler flag a new tab.
const EVENT_TAB_SET: Record<EventTabId, true> = {
    set_maintenance: true,
    work_order: true,
    intervention: true,
    observation: true,
    improvement: true,
};
const EVENT_TABS = Object.keys(EVENT_TAB_SET) as EventTabId[];

export const TAB_PARAM = 'tab';
const CONVERSATION_PARAM = 'conversation';
const EVENT_PARAM = 'event';
/** Opens the event modal on this tab. Notification emails send it, so the backend builds it too. */
export const NEW_EVENT_PARAM = 'new_event';

/**
 * Link into a machine's Summary route, landing its asset drawer on `tab`.
 *
 * `conversationId` names which thread the Chat tab should open. A machine can carry more than one —
 * its own default plus any custom group — and without it the tab falls back to the default, which
 * is the wrong one for a notification about a group.
 */
export function assetDrawerUrl(nodeId: number, tab: AssetDrawerTab, conversationId?: number): string {
    const query = new URLSearchParams({ [TAB_PARAM]: tab });
    if (conversationId !== undefined) {
        query.set(CONVERSATION_PARAM, String(conversationId));
    }
    return `/dashboard/summary/${nodeId}?${query.toString()}`;
}

/**
 * The tab a `?tab=` deep link asks for, or `undefined` when it names none we have. A query string is
 * user input — a pasted link, a stale bookmark — so an unknown value is discarded rather than passed
 * on to the drawer, which would render a tabless body for it.
 */
export function assetDrawerTabFromSearch(search: string): AssetDrawerTab | undefined {
    const requested = new URLSearchParams(search).get(TAB_PARAM);
    return TABS.find((tab) => tab === requested);
}

/** The event-modal tab a `?new_event=` link asks for, or `undefined` when it names none we have. */
export function newEventTabFromSearch(search: string): EventTabId | undefined {
    const requested = new URLSearchParams(search).get(NEW_EVENT_PARAM);
    return EVENT_TABS.find((tab) => tab === requested);
}

/**
 * The conversation a `?conversation=` deep link asks for, or `undefined` when it names none.
 *
 * Same reason the tab is validated: this is user input. Anything that is not a positive integer is
 * discarded here rather than travelling on as a `NaN` id nothing downstream would match. An id that
 * parses but no longer resolves is NOT this function's problem — the drawer falls back to the
 * default conversation for it, which is what a stale link deserves.
 */
export function assetDrawerConversationFromSearch(search: string): number | undefined {
    const requested = new URLSearchParams(search).get(CONVERSATION_PARAM);
    if (requested === null || !/^\d+$/.test(requested)) {
        return undefined;
    }
    const id = Number(requested);
    return id > 0 ? id : undefined;
}

/**
 * Link into a machine's History tab, with `eventKey` (a feed item's `${type}:${source_id}`)
 * expanded: where a feed card leads on the mobile navigation, which shows the asset as a page.
 */
export function assetHistoryUrl(nodeId: number, eventKey?: string): string {
    const query = new URLSearchParams({ [TAB_PARAM]: 'history' });
    if (eventKey !== undefined) {
        query.set(EVENT_PARAM, eventKey);
    }
    return `/dashboard/summary/${nodeId}?${query.toString()}`;
}

/** The event a `?event=` deep link asks the History tab to expand, or `undefined` for none we can read. */
export function assetDrawerEventFromSearch(search: string): string | undefined {
    const requested = new URLSearchParams(search).get(EVENT_PARAM);
    return requested !== null && EVENT_KEY.test(requested) ? requested : undefined;
}

/**
 * The same query string with `tab` swapped, everything else kept.
 *
 * Switching tabs must not rewrite the whole search: a link that named a conversation
 * (`?tab=chat&conversation=12`) would lose it on the way to another tab, and coming back would
 * open the node's default thread instead of the one the link asked for.
 */
export function withTab(search: string, tab: string): string {
    const query = new URLSearchParams(search);
    query.set(TAB_PARAM, tab);
    return `?${query.toString()}`;
}
